import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { TrendUp, TrendDown, Scales, CurrencyDollar, Shrimp, Warning } from "@phosphor-icons/react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

export default function LabaRugiPage() {
  const [siklusList, setSiklusList] = useState([]);
  const [selectedSiklus, setSelectedSiklus] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/siklus?limit=500").then(r => setSiklusList(r.data.data)); }, []);

  useEffect(() => {
    if (!selectedSiklus) return;
    setLoading(true);
    Promise.all([
      api.get(`/siklus/${selectedSiklus}/profit-loss`),
      api.get(`/siklus/${selectedSiklus}/performance`)
    ]).then(([pl, perf]) => {
      setData({ ...pl.data, performance: perf.data });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [selectedSiklus]);

  const fmt = (v) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(v || 0);

  return (
    <div data-testid="laba-rugi-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "Work Sans" }}>Laba / Rugi Siklus</h1>
          <p className="text-sm text-slate-500 mt-1">Analisis keuangan per siklus budidaya</p>
        </div>
        <Select value={selectedSiklus} onValueChange={setSelectedSiklus}>
          <SelectTrigger data-testid="select-siklus-pl" className="w-[280px]"><SelectValue placeholder="Pilih Siklus" /></SelectTrigger>
          <SelectContent>{siklusList.map(s => <SelectItem key={s._id} value={s._id}>{s.tambak_nama} - {s.tanggal_tebar}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!selectedSiklus ? (
        <Card className="bg-white border border-slate-200"><CardContent className="py-16 text-center text-slate-400">Pilih siklus untuk melihat analisis laba/rugi</CardContent></Card>
      ) : loading ? (
        <div className="text-center py-16 text-slate-400">Memuat...</div>
      ) : data ? (
        <>
          {/* KPI Row */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Card className="bg-white border border-slate-200"><CardContent className="p-4">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Pendapatan</p>
              <p className="text-lg font-bold text-emerald-700 mt-1" style={{ fontFamily: "Work Sans" }}>{fmt(data.pendapatan?.penjualan_udang)}</p>
              <p className="text-[10px] text-slate-400">{data.pendapatan?.total_kg} kg terjual</p>
            </CardContent></Card>
            <Card className="bg-white border border-slate-200"><CardContent className="p-4">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Total Biaya</p>
              <p className="text-lg font-bold text-red-600 mt-1" style={{ fontFamily: "Work Sans" }}>{fmt(data.biaya?.total)}</p>
              <p className="text-[10px] text-slate-400">{data.biaya?.pakan_kg} kg pakan</p>
            </CardContent></Card>
            <Card className={`border ${data.laba_rugi >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}><CardContent className="p-4">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{data.laba_rugi >= 0 ? "LABA" : "RUGI"}</p>
              <p className={`text-lg font-bold mt-1 ${data.laba_rugi >= 0 ? "text-emerald-700" : "text-red-700"}`} style={{ fontFamily: "Work Sans" }}>{fmt(Math.abs(data.laba_rugi))}</p>
              <p className="text-[10px] text-slate-400">Margin: {data.margin_persen}%</p>
            </CardContent></Card>
            <Card className="bg-white border border-slate-200"><CardContent className="p-4">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">FCR</p>
              <p className="text-lg font-bold text-[#1A1A1A] mt-1" style={{ fontFamily: "Work Sans" }}>{data.fcr ?? "-"}</p>
              <p className="text-[10px] text-slate-400">{data.fcr && data.fcr < 1.5 ? "Sangat baik" : data.fcr && data.fcr < 2 ? "Baik" : data.fcr ? "Perlu perbaikan" : ""}</p>
            </CardContent></Card>
            <Card className="bg-white border border-slate-200"><CardContent className="p-4">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">BEP</p>
              <p className="text-lg font-bold text-[#1A1A1A] mt-1" style={{ fontFamily: "Work Sans" }}>{data.bep_kg ? `${data.bep_kg} kg` : "-"}</p>
              <p className="text-[10px] text-slate-400">Break Even Point</p>
            </CardContent></Card>
          </div>

          {/* Biaya Breakdown + Growth Curve */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-white border border-slate-200">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>Rincian Biaya</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { label: "Benur", value: data.biaya?.benur, color: "bg-blue-500" },
                    { label: "Pakan", value: data.biaya?.pakan, color: "bg-[#B8860B]" },
                    { label: "Operasional", value: data.biaya?.operasional, color: "bg-slate-500" },
                  ].map((item, i) => {
                    const pct = data.biaya?.total > 0 ? (item.value / data.biaya.total * 100) : 0;
                    return (
                      <div key={i}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="font-medium text-slate-700">{item.label}</span>
                          <span className="text-slate-500">{fmt(item.value)} ({pct.toFixed(0)}%)</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${item.color} rounded-full`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                  <div className="pt-2 border-t flex justify-between text-sm font-semibold">
                    <span>Total Biaya</span><span className="text-red-600">{fmt(data.biaya?.total)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-200">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>Kurva Pakan Kumulatif</CardTitle></CardHeader>
              <CardContent className="px-2 pb-4">
                {data.performance?.growth_curve?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={data.performance.growth_curve} margin={{ top: 5, right: 5, left: -10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="doc" tick={{ fontSize: 10, fill: "#64748B" }} label={{ value: "DOC", position: "insideBottom", offset: -5, fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10, fill: "#64748B" }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="pakan_kumulatif" name="Pakan Kumulatif (kg)" stroke="#B8860B" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <p className="text-xs text-slate-400 text-center py-10">Belum ada data monitoring</p>}
              </CardContent>
            </Card>
          </div>

          {/* SR & Performance */}
          {data.performance && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Card className="bg-white border border-slate-200"><CardContent className="p-4 text-center">
                <Shrimp size={24} className="text-[#008080] mx-auto mb-1" weight="duotone" />
                <p className="text-xl font-bold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>{data.performance.sr_persen ?? "-"}%</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Survival Rate</p>
              </CardContent></Card>
              <Card className="bg-white border border-slate-200"><CardContent className="p-4 text-center">
                <Scales size={24} className="text-[#B8860B] mx-auto mb-1" weight="duotone" />
                <p className="text-xl font-bold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>{data.performance.total_pakan_kg} kg</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total Pakan</p>
              </CardContent></Card>
              <Card className="bg-white border border-slate-200"><CardContent className="p-4 text-center">
                <p className="text-xl font-bold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>{data.performance.hari_aktif}</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Hari Monitoring</p>
              </CardContent></Card>
              <Card className="bg-white border border-slate-200"><CardContent className="p-4 text-center">
                <p className="text-xl font-bold text-[#1A1A1A]" style={{ fontFamily: "Work Sans" }}>{data.performance.jumlah_benur?.toLocaleString("id-ID")}</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Jumlah Benur</p>
              </CardContent></Card>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
